import { useCallback, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import {
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { atom, useAtomValue } from 'jotai';

import { useLocalizedFormatDate } from '../../../claims/freeClaims/state';
import {
  comparisonPeriodModeAtom,
  customPriorEndDateAtom,
  customPriorStartDateAtom,
  endDateAtom,
  startDateAtom,
} from '../atoms';

/** Preset key for "today-relative" date ranges (main picker and saved view relative dates). */
export type TDateRangePresetKey =
  | 'last30'
  | 'last60'
  | 'last90'
  | 'lastMonth'
  | 'mtd'
  | 'ytd';

/** Single source of truth: date range preset keys in display order. */
export const DATE_RANGE_PRESET_KEYS: readonly TDateRangePresetKey[] = [
  'last30',
  'last60',
  'last90',
  'lastMonth',
  'mtd',
  'ytd',
] as const;

/**
 * Hook: translated labels for date range presets. Use in components that render
 * preset buttons so Lingui macro can extract the strings. Single source of truth for keys and copy.
 */
export function useDatePresetLabels(): Record<TDateRangePresetKey, string> {
  const { t } = useLingui();
  return useMemo(
    () => ({
      last30: t`Last 30 days`,
      last60: t`Last 60 days`,
      last90: t`Last 90 days`,
      lastMonth: t`Last month`,
      mtd: t`Month to date`,
      ytd: t`Year to date`,
    }),
    [t],
  );
}

/**
 * Format a date range for display (e.g. "PP – PP", locale long date).
 * Prefer useFormatDateRangeDisplay() so dates follow the user's locale.
 */
export function formatDateRangeDisplay(
  start: Date,
  end: Date,
  formatDate?: (date: Date) => string,
): string {
  const fmt = formatDate ?? ((d: Date) => format(d, 'PP'));
  return `${fmt(start)} – ${fmt(end)}`;
}

/**
 * Hook: format a date range for display using the user's locale.
 * Use this instead of formatDateRangeDisplay() in components so dates are localized.
 */
export function useFormatDateRangeDisplay(): (
  start: Date,
  end: Date,
) => string {
  const formatDate = useLocalizedFormatDate();
  return useCallback(
    (start: Date, end: Date) =>
      `${formatDate(start.toISOString(), false, 'PP')} – ${formatDate(end.toISOString(), false, 'PP')}`,
    [formatDate],
  );
}

/**
 * Hook: date range preset options for the current day (value + getRange only).
 * Callers should build labels in the component with t from useLingui() so Lingui context is correct.
 */
export function useDateRangePresetOptions(): {
  options: {
    value: TDateRangePresetKey;
    getRange: () => { start: Date; end: Date };
  }[];
  referenceDate: Date;
} {
  const referenceDate = startOfDay(new Date());
  const options = DATE_RANGE_PRESET_KEYS.map((value) => ({
    value,
    getRange: () => getRangeForPreset(value, referenceDate),
  }));
  return { options, referenceDate };
}

/**
 * Returns true if prior period is valid: prior end is strictly before current start (day boundary),
 * and priorStart <= priorEnd. Used when loading saved views and when clamping custom prior in UI.
 */
export function isValidPriorPeriod(
  priorStart: Date,
  priorEnd: Date,
  currentStart: Date,
): boolean {
  return (
    startOfDay(priorEnd) < startOfDay(currentStart) && priorStart <= priorEnd
  );
}

/**
 * Clamps a prior period so it ends before currentStart (at day boundary). Use in date picker onSelect.
 */
export function clampPriorPeriodToBeforeCurrent(
  from: Date | null,
  to: Date | null,
  currentStart: Date,
): { from: Date | null; to: Date | null } {
  const maxPriorEnd = startOfDay(subDays(currentStart, 1));
  if (to != null && startOfDay(to) >= startOfDay(currentStart)) {
    to = maxPriorEnd;
  }
  if (from != null && to != null && from > to) {
    from = to;
  }
  return { from, to };
}

/** Returns true if the value is a known preset key (e.g. when loading from API). */
export function isAllowedDateRangePreset(
  value: unknown,
): value is TDateRangePresetKey {
  return (
    typeof value === 'string' &&
    DATE_RANGE_PRESET_KEYS.includes(value as TDateRangePresetKey)
  );
}

/**
 * Returns the preset whose range matches the given start/end (compared at day boundary).
 * Uses referenceDate (default: today) for preset range calculation.
 * Returns null if no preset matches.
 */
export function getMatchingDatePreset(
  startDate: Date,
  endDate: Date,
  referenceDate: Date = new Date(),
): { value: TDateRangePresetKey } | null {
  const value = DATE_RANGE_PRESET_KEYS.find((key) => {
    const { start, end } = getRangeForPreset(key, referenceDate);
    return (
      startOfDay(startDate).getTime() === startOfDay(start).getTime() &&
      startOfDay(endDate).getTime() === startOfDay(end).getTime()
    );
  });
  return value != null ? { value } : null;
}

/** Trailing-days presets: key → days back from today (inclusive). */
const TRAILING_DAYS: Record<'last30' | 'last60' | 'last90', number> = {
  last30: 29,
  last60: 59,
  last90: 89,
};

/**
 * Returns the date range for a preset, relative to a reference date (default: today).
 * Used by the main date picker presets and by saved views when loading a relative date range.
 */
export function getRangeForPreset(
  presetKey: TDateRangePresetKey,
  referenceDate: Date = new Date(),
): { start: Date; end: Date } {
  const today = startOfDay(referenceDate);
  const trailing = TRAILING_DAYS[presetKey as keyof typeof TRAILING_DAYS];
  if (trailing != null) {
    return { start: subDays(today, trailing), end: today };
  }
  switch (presetKey) {
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    }
    case 'mtd':
      return { start: startOfMonth(today), end: today };
    case 'ytd':
      return { start: startOfYear(today), end: today };
    default:
      return { start: subDays(today, TRAILING_DAYS.last30), end: today };
  }
}

/**
 * Same-length prior period ending the day before current period start.
 * Used by "Trailing days" mode and the "Same length" comparison preset.
 */
export function getTrailingPriorPeriod(
  startDate: Date,
  endDate: Date,
): { start: Date; end: Date } {
  const end = endOfDay(subDays(startDate, 1));
  const start = startOfDay(
    subDays(
      new Date(startDate.getTime() - (endDate.getTime() - startDate.getTime())),
      1,
    ),
  );
  return { start, end };
}

/**
 * Resolves the prior (comparison) period from current range and comparison mode.
 * Used by both clinical and hygiene data and by the Filters UI.
 */
export function getPriorPeriod(
  startDate: Date,
  endDate: Date,
  mode: 'trailing' | 'yoy' | 'custom',
  customPriorStart: Date | null,
  customPriorEnd: Date | null,
): { priorStart: Date; priorEnd: Date } {
  if (mode === 'custom' && customPriorStart != null && customPriorEnd != null) {
    const priorStart = startOfDay(customPriorStart);
    const priorEnd = endOfDay(customPriorEnd);
    if (isValidPriorPeriod(priorStart, priorEnd, startDate)) {
      return { priorStart, priorEnd };
    }
  }
  if (mode === 'yoy') {
    return {
      priorStart: startOfDay(subYears(startDate, 1)),
      priorEnd: endOfDay(subYears(endDate, 1)),
    };
  }
  const { start, end } = getTrailingPriorPeriod(startDate, endDate);
  return { priorStart: start, priorEnd: end };
}

/** Derived atom: prior (comparison) period from insights filter atoms. */
export const priorPeriodAtom = atom((get) => {
  const startDate = get(startDateAtom);
  const endDate = get(endDateAtom);
  const mode = get(comparisonPeriodModeAtom);
  const customPriorStart = get(customPriorStartDateAtom);
  const customPriorEnd = get(customPriorEndDateAtom);
  return getPriorPeriod(
    startDate,
    endDate,
    mode,
    customPriorStart,
    customPriorEnd,
  );
});

/** Returns the prior (comparison) period from insights filter atoms. */
export function usePriorPeriod() {
  return useAtomValue(priorPeriodAtom);
}

/** Comparison preset keys in display order. Single source of truth for keys and type. */
const COMPARISON_PRESET_KEYS = [
  'sameLength',
  'previousMonth',
  'sameMonthLastYear',
] as const;

export type TComparisonPresetKey = (typeof COMPARISON_PRESET_KEYS)[number];

function getComparisonPresetRange(
  key: TComparisonPresetKey,
  startDate: Date,
  endDate: Date,
): { start: Date; end: Date } {
  switch (key) {
    case 'sameLength':
      return getTrailingPriorPeriod(startDate, endDate);
    case 'previousMonth':
      return {
        start: startOfMonth(subMonths(startDate, 1)),
        end: endOfMonth(subMonths(startDate, 1)),
      };
    case 'sameMonthLastYear':
      return {
        start: startOfMonth(subYears(startDate, 1)),
        end: endOfMonth(subYears(startDate, 1)),
      };
    default:
      return getTrailingPriorPeriod(startDate, endDate);
  }
}

/**
 * Returns the prior period range for each comparison preset, given the current period.
 * Used by the custom comparison date picker. Callers build labels in the component with t from useLingui().
 */
export function getComparisonPresetRanges(
  startDate: Date,
  endDate: Date,
): { key: TComparisonPresetKey; start: Date; end: Date }[] {
  return COMPARISON_PRESET_KEYS.map((key) => ({
    key,
    ...getComparisonPresetRange(key, startDate, endDate),
  }));
}
